import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Routes } from '../../enums/Routes';
import { useGetTeamsQuery } from '../../hooks';
import {
  buildDashboardBreadcrumbs,
  buildHRBreadcrumbs,
  buildTeamsBreadcrumbs,
  buildOrganizationBreadcrumbs,
  buildContentSystemBreadcrumbs,
  buildCustomSurveyBreadcrumbs,
} from './breadcrumbBuilders';

export type Breadcrumb = {
  label: string;
  path: string;
};

const extractBaseRoute = (route: string, segmentCount: number = 1): string => {
  const segments = route.split('/').filter(Boolean);
  return '/' + segments.slice(0, segmentCount).join('/');
};

const ROUTE_BASES = {
  dashboard: Routes.Dashboard,
  hrDashboard: Routes.HRDashboard,
  teams: Routes.Teams,
  organization: extractBaseRoute(Routes.OrganizationTeamsOverview, 1),
  contentSystem: extractBaseRoute(Routes.ContentManagementSystemArticles, 1),
} as const;

export const useBreadcrumbs = (pathname: string): Breadcrumb[] => {
  const params = useParams();
  const { data: teams } = useGetTeamsQuery();

  return useMemo(() => {
    const getTeamName = (teamId: string): string => {
      const team = teams?.find((t) => t.id === Number(teamId));
      return team?.name || 'Team';
    };

    if (pathname.startsWith(ROUTE_BASES.dashboard)) {
      // Check if it's a custom survey route
      if (pathname.includes('/custom-surveys')) {
        return buildCustomSurveyBreadcrumbs(pathname, params);
      }
      // Otherwise use default dashboard breadcrumbs (work environment)
      return buildDashboardBreadcrumbs(pathname, params);
    }

    if (pathname.startsWith(ROUTE_BASES.hrDashboard)) {
      return buildHRBreadcrumbs(pathname, params);
    }

    if (pathname.startsWith(ROUTE_BASES.teams)) {
      return buildTeamsBreadcrumbs(pathname, params, getTeamName);
    }

    if (pathname.startsWith(ROUTE_BASES.organization)) {
      return buildOrganizationBreadcrumbs(pathname, params, getTeamName);
    }

    if (pathname.startsWith(ROUTE_BASES.contentSystem)) {
      return buildContentSystemBreadcrumbs(pathname, params);
    }

    return [];
  }, [pathname, params, teams]);
};
